/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, July 2020
 */

import React, { useEffect, useReducer, useRef } from 'react';
import ReactDOM from 'react-dom';
import Popup from 'reactjs-popup';
import { styled, Theme, TouchableOpacity } from '@staffino/primitive-controls';

const CloseButton = styled(TouchableOpacity)({
  cursor: 'pointer',
  position: 'absolute',
  zIndex: 1,
  height: '2.5rem',
  width: '2.5rem',
  lineHeight: '2.5rem',
  fontSize: '1.5rem',
  textAlign: 'center',
  borderRadius: '50%',
  backgroundColor: '#e5e9f2',
  right: '10px',
  top: '42px',
  color: '#8492a6',
  transition: 'all 0.2s',
  '&:hover': {
    color: 'white',
    backgroundColor: '#058cf0',
  },
});

type StyledPopupProps = {
  autoSize?: boolean;
  theme?: Theme;
};

const StyledPopup = styled(Popup)<StyledPopupProps>(
  (props: StyledPopupProps) => ({
    '&-overlay': {
      overflowY: 'auto' as const,
      backgroundColor: `rgba(0, 0, 0, 0.3) !important`,
    },
    '&-content': {
      display: 'flex' as const,
      flexDirection: 'column' as const,
      width: '100% !important',
      backgroundColor: 'transparent !important',
      border: 'none !important',
      [props.theme.mq.medium]: {
        width: 'auto',
        maxWidth: '48rem',
        padding: '2rem 0 !important',
        margin: '2rem auto',
        height: props.autoSize ? '100%' : 'auto',
      },

      '& > iframe': {
        background: 'white',
        boxShadow: `0rem 0.15rem 0.5rem 0rem rgba(0, 0, 0, 0.5)`,
      },
    },
  }),
);

type Props = {
  autoSize?: boolean;
  children?: React.ReactNode;
  onClose?: () => void;
};
const Modal: React.FunctionComponent<Props> = ({
  children,
  onClose,
  autoSize,
}: Props) => {
  const nodeRef = useRef(undefined);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const modalRoot = document.getElementById('modal-root');
    const element = document.createElement('div');
    modalRoot.appendChild(element);
    nodeRef.current = element;
    forceUpdate();
    return () => {
      modalRoot.removeChild(element);
    };
  }, [nodeRef, forceUpdate]);

  if (!nodeRef.current) {
    return null;
  }
  return ReactDOM.createPortal(
    <StyledPopup
      open
      closeOnDocumentClick
      onClose={onClose}
      autoSize={autoSize}
    >
      <>
        <CloseButton onPress={() => onClose?.()}>&times;</CloseButton>
        {children}
      </>
    </StyledPopup>,
    nodeRef.current,
  );
};

export default Modal;
